import {useMemo} from "react";
import {usePrograms} from "@/hooks/usePrograms";
import LoadingGridItems from "@/components/LoadingGridItems.tsx";
import {MetaTags} from "@/hooks/MetaTags.tsx";

function CardSkeleton() {
    return (
        <div className="relative isolate flex flex-col gap-8 lg:flex-row animate-pulse">
            <div className="relative aspect-video sm:aspect-2/1 lg:aspect-4/1 lg:w-86 lg:shrink-0">
                <div className="absolute inset-0 size-full rounded-2xl bg-gray-100"/>
            </div>
            <div className="flex-1">
                <div className="h-4 w-40 rounded bg-gray-100"/>
                <div className="mt-3 h-6 w-3/5 rounded bg-gray-100"/>
                <div className="mt-4 space-y-2">
                    <div className="h-4 w-full rounded bg-gray-100"/>
                    <div className="h-4 w-4/5 rounded bg-gray-100"/>
                </div>
                <div className="mt-6 h-5 w-40 rounded bg-gray-100"/>
            </div>
        </div>
    );
}

const DAY_ORDER = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_LABEL: Record<string, string> = {
    MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed", THURSDAY: "Thu",
    FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun"
};

function compressRanges(days: string[]): string {
    if (!days.length) return "";
    const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const idx = (d: string) => order.indexOf(d);
    const sorted = days.slice().sort((a, b) => idx(a) - idx(b));
    const ranges: Array<{ start: string, end: string }> = [];
    let start = sorted[0], prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        if (idx(cur) === idx(prev) + 1) {
            prev = cur;
            continue;
        }
        ranges.push({start, end: prev});
        start = prev = cur;
    }
    ranges.push({start, end: prev});
    return ranges.map(r => r.start === r.end ? r.start : `${r.start}–${r.end}`).join(", ");
}

function summarizeSchedules(schedules?: Array<{
    dayOfWeek: string; startTime: string; endTime: string; producerOverride?: string | null
}>): string {
    if (!schedules || schedules.length === 0) return "—";
    const key = (s: any) => `${s.startTime}-${s.endTime}-${s.producerOverride ?? ""}`;
    const groups = new Map<string, any[]>();
    schedules.forEach(s => {
        const k = key(s);
        groups.set(k, [...(groups.get(k) ?? []), s]);
    });

    const parts: string[] = [];
    for (const [k, arr] of groups.entries()) {
        const sorted = arr.slice().sort(
            (a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek)
        );
        const dayLabels = sorted.map((s: any) => DAY_LABEL[s.dayOfWeek]);
        const dayText = compressRanges(dayLabels);
        const [start, end, prod] = k.split("-");
        parts.push(prod ? `${dayText} ${start}–${end} (${prod})` : `${dayText} ${start}–${end}`);
    }
    return parts.join("; ");
}

const WEEKEND = new Set(["SATURDAY", "SUNDAY"]);

function isWeekendOnly(p: any): boolean {
    const days = (p?.schedules ?? []).map((s: any) => s?.dayOfWeek).filter(Boolean);
    if (days.length === 0) return false; // don’t treat “unscheduled” as weekend
    return days.every((d: string) => WEEKEND.has(d));
}

// pick earliest HH:mm from schedules (lexical works for 24h strings)
function earliestStartTime(p: any): string {
    const times = (p?.schedules ?? [])
        .map((s: any) => s?.startTime)
        .filter((t: unknown): t is string => typeof t === "string" && /^\d{2}:\d{2}$/.test(t));
    if (times.length === 0) return "99:99"; // push to end within each group
    return times.sort()[0];
}

export default function ProgramsPage() {
    const {data, isLoading, isError} = usePrograms({page: 0, size: 100, sortBy: "title", sortOrder: "asc"});

    const programs = useMemo(() => {
        const items = (data?.items ?? []).slice();

        // 1) Weekdays first (isWeekendOnly false), 2) Weekend-only last, each by earliest start time
        items.sort((a: any, b: any) => {
            const aw = isWeekendOnly(a) ? 1 : 0;
            const bw = isWeekendOnly(b) ? 1 : 0;
            if (aw !== bw) return aw - bw; // 0 (weekday/mixed) before 1 (weekend-only)
            return earliestStartTime(a).localeCompare(earliestStartTime(b));
        });

        return items;
    }, [data]);

    return (
        <>
            <MetaTags
                title={"Kosmos 94.1 - Programme"}
                description={"Ontdek ons diverse programme wat jou die hele dag deur ingelig en vermaak."}
                url={"/op-die-lug/programme"}
            />

            <section className="bg-white py-16 sm:py-22">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-4xl text-center">
                        <h2 className="text-4xl font-semibold tracking-tight text-blue-900 sm:text-5xl">
                            Programme
                        </h2>
                        <p className="mt-2 text-lg/8 text-gray-600">
                            Ons programme bied 'n verskeidenheid inhoud, van nuus tot vermaak, wat jou die hele dag deur
                            ingelig hou. Of jy nou op soek is na 'n oggendritueel, 'n middagete-onderbreking of 'n
                            ontspanne
                            aand, ons het iets vir almal. Ontdek ons programme hier.
                        </p>
                    </div>

                    {isLoading && <LoadingGridItems count={3}/>}

                    {isError && <p className="mt-6 text-sm text-red-600 text-center">Failed to load Programs.</p>}

                    {programs.length === 0 && !isLoading && !isError && (
                        <p className="mt-6 text-sm text-gray-600 text-center">No Programs found.</p>
                    )}

                    <div
                        className="mt-8 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                        {programs.map((post: any) => (
                            <article key={post.id} className="flex flex-col items-start justify-between">

                                {/* Media */}
                                {post.imageUrl && (
                                    <div className="relative">
                                        <div className="relative w-full">
                                            <img
                                                alt={post.title}
                                                src={post.imageUrl}
                                                className="w-full rounded-2xl bg-gray-100 object-cover aspect-video sm:aspect-[2/1] lg:aspect-[3/2]"
                                            />
                                            <div className="absolute inset-0 rounded-2xl ring-1 ring-gray-900/10"/>
                                        </div>
                                    </div>
                                )}

                                {/* Meta + Content */}
                                <div className="flex max-w-xl grow flex-col justify-between">
                                    <div className="mt-4 flex items-center gap-x-4 text-sm">
                                    <span className="text-gray-600">
                                        {summarizeSchedules(post.schedules)}
                                    </span>
                                    </div>

                                    <div className="group relative grow">
                                        <h3 className="mt-4 text-lg/6 font-semibold text-blue-900 group-hover:text-gray-600">
                                            {post.title}
                                        </h3>
                                        <p className="mt-2 text-md/6 text-gray-600">{post.description}</p>
                                        <p className="mt-2 text-md/6 font-semibold text-gray-600">Met {post.producer}</p>
                                    </div>
                                </div>
                            </article>
                        ))}

                    </div>
                </div>
            </section>
        </>
    )
}
